/**
 * ActionDefStorage — manages local storage of ActionDef content records.
 *
 * ActionDef content arrives as a ContentType='ActionDef' record from the backend.
 * The content_body column contains base64-encoded JSON that matches the C# ActionDefDto
 * structure (PascalCase on the wire).
 *
 * Storage structure:
 *   Key:   action_def_{languageTypeId}   e.g. action_def_1
 *   Value: JSON-serialised ActionDefDto (camelCase)
 *   Index: action_def_index              array of stored languageTypeIds
 *
 * Wire format (base64 → JSON, PascalCase):
 * {
 *   "Source": "...",
 *   "LanguageTypeId": "1",
 *   "Extra": "...",
 *   "ActionGroups": [ { "Group": "...", "Name": "...", "ActionMVars": [ { "MVar": "...", "Text": "...", "Actions": [...] } ] } ],
 *   "ActionTypes":  [ { "Action": "...", "Name": "...", "Schedule": [...], "Expense": [...], "Importance": [...], "Outcome": [...] } ]
 * }
 */

import type { ContentRecord } from '../stores/contentStore';

// Re-use the same StorageInterface shape used throughout the codebase
export interface StorageInterface {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
}

// ─── DTO interfaces (camelCase, mirrors C# PascalCase) ───────────────────────

export interface ActionScheduleDto {
  text: string;
  value: string;
}

export interface ActionExpenseDto {
  text: string;
  value: string;
}

export interface ActionImportanceDto {
  text: string;
  value: string;
}

export interface ActionOutcomeDto {
  text: string;
  value: string;
}

export interface ActionTypeDto {
  /** Action identifier (e.g. "track", "manage", "referral") */
  action: string;
  /** Display name */
  name: string;
  /** Default schedule value (matches an ActionScheduleDto.value) */
  default: string;
  schedule:   ActionScheduleDto[];
  expense:    ActionExpenseDto[];
  importance: ActionImportanceDto[];
  outcome:    ActionOutcomeDto[];
}

export interface ActionMVarDto {
  /** MVar name linking this group entry to a concern mvarname (e.g. "cn_behavior_1") */
  mvar: string;
  /** Display text for this action link */
  text: string;
  /** List of action identifiers supported for this MVar (references ActionTypeDto.action) */
  actions: string[];
}

export interface ActionGroupDto {
  /** Group key (e.g. "behavior", "family") */
  group: string;
  /** Display name for this group */
  name: string;
  actionMVars: ActionMVarDto[];
}

export interface ActionDefDto {
  source: string;
  languageTypeId: string;
  extra: string;
  actionGroups: ActionGroupDto[];
  actionTypes:  ActionTypeDto[];
  /** ISO timestamp set when the record was stored locally */
  downloadedAt: string;
}

// ─── Storage class ────────────────────────────────────────────────────────────

export class ActionDefStorage {
  private readonly keyPrefix = 'action_def_';
  private readonly indexKey  = 'action_def_index';

  constructor(private storage: StorageInterface) {}

  // ─── Private helpers ────────────────────────────────────────────────────────

  private storageKey(languageTypeId: string): string {
    return `${this.keyPrefix}${languageTypeId}`;
  }

  private decodeBase64(b64: string): string {
    try {
      const bufferApi = (globalThis as any).Buffer;
      if (bufferApi?.from) {
        return bufferApi.from(b64, 'base64').toString('utf-8');
      }
      return atob(b64);
    } catch (err) {
      throw new Error(`ActionDefStorage: failed to decode base64 — ${err}`);
    }
  }

  private async addToIndex(languageTypeId: string): Promise<void> {
    try {
      const raw = await this.storage.getItem(this.indexKey);
      const ids: string[] = raw ? JSON.parse(raw) : [];
      if (!ids.includes(languageTypeId)) {
        ids.push(languageTypeId);
        await this.storage.setItem(this.indexKey, JSON.stringify(ids));
      }
    } catch (_) { /* non-fatal */ }
  }

  private async removeFromIndex(languageTypeId: string): Promise<void> {
    try {
      const raw = await this.storage.getItem(this.indexKey);
      const ids: string[] = raw ? JSON.parse(raw) : [];
      const updated = ids.filter(id => id !== languageTypeId);
      await this.storage.setItem(this.indexKey, JSON.stringify(updated));
    } catch (_) { /* non-fatal */ }
  }

  private async getIndex(): Promise<string[]> {
    try {
      const raw = await this.storage.getItem(this.indexKey);
      return raw ? JSON.parse(raw) : [];
    } catch (_) {
      return [];
    }
  }

  // ─── Public API ──────────────────────────────────────────────────────────────

  /**
   * Persist an ActionDefDto keyed by languageTypeId.
   */
  async saveActionDef(dto: ActionDefDto): Promise<boolean> {
    try {
      const record: ActionDefDto = { ...dto, downloadedAt: dto.downloadedAt || new Date().toISOString() };
      const langId = record.languageTypeId || 'unknown';
      await this.storage.setItem(this.storageKey(langId), JSON.stringify(record));
      await this.addToIndex(langId);
      console.log(`✅ ActionDefStorage: saved ActionDef languageTypeId="${langId}" (${record.actionGroups.length} groups, ${record.actionTypes.length} types)`);
      return true;
    } catch (err) {
      console.error('❌ ActionDefStorage.saveActionDef failed:', err);
      return false;
    }
  }

  /**
   * Retrieve a stored ActionDefDto by languageTypeId.
   * Falls back to the first available record when languageTypeId is omitted.
   */
  async getActionDef(languageTypeId?: string): Promise<ActionDefDto | null> {
    try {
      if (languageTypeId) {
        const raw = await this.storage.getItem(this.storageKey(languageTypeId));
        return raw ? (JSON.parse(raw) as ActionDefDto) : null;
      }
      // Fallback: return first available
      const ids = await this.getIndex();
      if (ids.length === 0) return null;
      const raw = await this.storage.getItem(this.storageKey(ids[0]));
      return raw ? (JSON.parse(raw) as ActionDefDto) : null;
    } catch (err) {
      console.error('❌ ActionDefStorage.getActionDef failed:', languageTypeId, err);
      return null;
    }
  }

  /**
   * Return all stored ActionDefDto records.
   */
  async getAllActionDefs(): Promise<ActionDefDto[]> {
    const ids = await this.getIndex();
    const results: ActionDefDto[] = [];
    for (const id of ids) {
      const dto = await this.getActionDef(id);
      if (dto) results.push(dto);
    }
    return results;
  }

  /**
   * Delete an ActionDefDto by languageTypeId.
   */
  async deleteActionDef(languageTypeId: string): Promise<boolean> {
    try {
      await this.storage.removeItem(this.storageKey(languageTypeId));
      await this.removeFromIndex(languageTypeId);
      console.log(`🗑️ ActionDefStorage: deleted languageTypeId="${languageTypeId}"`);
      return true;
    } catch (err) {
      console.error('❌ ActionDefStorage.deleteActionDef failed:', languageTypeId, err);
      return false;
    }
  }

  /**
   * Remove all stored ActionDef records and the index.
   */
  async clearAll(): Promise<void> {
    const ids = await this.getIndex();
    for (const id of ids) {
      try { await this.storage.removeItem(this.storageKey(id)); } catch (_) { /* continue */ }
    }
    try { await this.storage.removeItem(this.indexKey); } catch (_) { /* non-fatal */ }
    console.log('🗑️ ActionDefStorage: cleared all records');
  }

  // ─── Query helpers ────────────────────────────────────────────────────────────

  /**
   * Return the list of action identifiers defined for a given MVar name.
   * Searches all groups across all stored ActionDef records for the matching MVar.
   * @param mvar  MVar name (case-insensitive, e.g. "cn_behavior_1")
   * @param languageTypeId  Optional — defaults to first available
   */
  async getActionsForMVar(mvar: string, languageTypeId?: string): Promise<string[]> {
    const dto = await this.getActionDef(languageTypeId);
    if (!dto) return [];
    const lower = mvar.toLowerCase();
    for (const group of dto.actionGroups) {
      for (const entry of group.actionMVars) {
        if (entry.mvar.toLowerCase() === lower) return entry.actions;
      }
    }
    return [];
  }

  /**
   * Return the ActionTypeDto for a given action identifier.
   * @param actionId  e.g. "track", "manage"
   * @param languageTypeId  Optional — defaults to first available
   */
  async getActionType(actionId: string, languageTypeId?: string): Promise<ActionTypeDto | null> {
    const dto = await this.getActionDef(languageTypeId);
    if (!dto) return null;
    const lower = actionId.toLowerCase();
    return dto.actionTypes.find(t => t.action.toLowerCase() === lower) ?? null;
  }

  // ─── Content-record unpacking ─────────────────────────────────────────────────

  /**
   * Unpack a ContentStore record of type "ActionDef" and persist it.
   *
   * Expected record.tableData shape (standard content-server response):
   *   Columns: [..., "content_body", ...]
   *   Rows:    [[..., "<base64-encoded JSON>", ...]]
   *
   * The base64-encoded JSON payload must match the C# ActionDefDto structure.
   * Returns the stored dto on success, null on failure.
   */
  async unpackFromContentRecord(record: ContentRecord): Promise<ActionDefDto | null> {
    try {
      console.log('\n🔍 ActionDefStorage.unpackFromContentRecord called for:', record.id);

      const tableData = (record as any).tableData;
      if (!tableData?.Data?.Columns || !tableData?.Data?.Rows?.length) {
        console.warn('⚠️ ActionDefStorage: ContentRecord has no tableData rows');
        return null;
      }

      const columns: string[] = tableData.Data.Columns;
      const bodyIdx = columns.findIndex((c: string) => c.toLowerCase() === 'content_body');
      if (bodyIdx === -1) {
        console.warn('⚠️ ActionDefStorage: content_body column not found. Available:', columns);
        return null;
      }

      const firstRow = tableData.Data.Rows[0];
      const encodedBody: string = firstRow[bodyIdx];
      if (!encodedBody) {
        console.warn('⚠️ ActionDefStorage: content_body is empty');
        return null;
      }

      const jsonString = this.decodeBase64(encodedBody);
      const p = JSON.parse(jsonString); // p = parsed PascalCase object from C# backend

      // ── Map PascalCase → camelCase ──────────────────────────────────────────

      const mapSchedule   = (arr: any[]): ActionScheduleDto[]   => (arr ?? []).map(x => ({ text: x.Text   ?? x.text   ?? '', value: x.Value   ?? x.value   ?? '' }));
      const mapExpense    = (arr: any[]): ActionExpenseDto[]    => (arr ?? []).map(x => ({ text: x.Text   ?? x.text   ?? '', value: x.Value   ?? x.value   ?? '' }));
      const mapImportance = (arr: any[]): ActionImportanceDto[] => (arr ?? []).map(x => ({ text: x.Text   ?? x.text   ?? '', value: x.Value   ?? x.value   ?? '' }));
      const mapOutcome    = (arr: any[]): ActionOutcomeDto[]    => (arr ?? []).map(x => ({ text: x.Text   ?? x.text   ?? '', value: x.Value   ?? x.value   ?? '' }));

      const actionTypes: ActionTypeDto[] = (p.ActionTypes ?? p.actionTypes ?? []).map((t: any): ActionTypeDto => ({
        action:     t.Action     ?? t.action     ?? '',
        name:       t.Name       ?? t.name       ?? '',
        default:    t.Default    ?? t.default    ?? '',
        schedule:   mapSchedule(t.Schedule   ?? t.schedule   ?? []),
        expense:    mapExpense(t.Expense     ?? t.expense     ?? []),
        importance: mapImportance(t.Importance ?? t.importance ?? []),
        outcome:    mapOutcome(t.Outcome     ?? t.outcome     ?? []),
      }));

      const actionGroups: ActionGroupDto[] = (p.ActionGroups ?? p.actionGroups ?? []).map((g: any): ActionGroupDto => ({
        group: g.Group ?? g.group ?? '',
        name:  g.Name  ?? g.name  ?? '',
        actionMVars: (g.ActionMVars ?? g.actionMVars ?? []).map((m: any): ActionMVarDto => ({
          mvar:    m.MVar    ?? m.mvar    ?? '',
          text:    m.Text    ?? m.text    ?? '',
          actions: m.Actions ?? m.actions ?? [],
        })),
      }));

      const dto: ActionDefDto = {
        source:        p.Source        ?? p.source        ?? '',
        languageTypeId: p.LanguageTypeId ?? p.languageTypeId ?? '1',
        extra:         p.Extra         ?? p.extra         ?? '',
        actionGroups,
        actionTypes,
        downloadedAt:  new Date().toISOString(),
      };

      console.log(`📋 ActionDefStorage: parsed ${dto.actionGroups.length} group(s), ${dto.actionTypes.length} type(s) for languageTypeId="${dto.languageTypeId}"`);

      const saved = await this.saveActionDef(dto);
      if (!saved) return null;

      console.log(`✅ ActionDefStorage: unpacked & stored ActionDef (source="${dto.source}", lang="${dto.languageTypeId}")`);
      return dto;
    } catch (err) {
      console.error('❌ ActionDefStorage.unpackFromContentRecord failed:', err);
      return null;
    }
  }
}
